import React, { ChangeEvent, useEffect, useState } from 'react'
import { HeroIcons } from '../../../lib/utils/heroIcons'
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Switch,
} from '@headlessui/react'
import { classNames } from '../../../lib/utils/classNames'
import { useTranslation } from 'next-i18next'
import { Property, PropertyWithAccess } from '../../../types/Property/Property'
import PropertyService from '../../../services/property.service'
import useLocale from '../../../hooks/useLocale'
import NavigationPropertyPreview from './NavigationPropertyPreview'
import { debounce } from '../../../lib/utils/debounce'

interface ListingNavigationProps {
  filtersObj: any[]
  currentPage: number
  maxPages: number
  onPageChange: (page: number) => void
  onOrderByChange: (orderBy: any) => void
  onChangeFilters: (onChangeFilters: any[]) => void
  onPropertyClick?: (property: PropertyWithAccess) => void
}

export default function ListingNavigation({
  currentPage,
  maxPages,
  onPageChange,
  onOrderByChange,
  onChangeFilters,
  onPropertyClick,
}: ListingNavigationProps) {
  const { t } = useTranslation('originals/listing')
  const { locale } = useLocale()

  const orderByOptions = [
    {
      id: 1,
      text: t('listingNavigation.orderByOptions.mostRecentFirst'),
      value: { orderBy: 'createdAt', order: 'DESC' },
    },
    {
      id: 2,
      text: t('listingNavigation.orderByOptions.oldestFirst'),
      value: { orderBy: 'createdAt', order: 'ASC' },
    },
    {
      id: 4,
      text: t('listingNavigation.orderByOptions.mostYieldFirst'),
      value: { orderBy: 'rentalYieldPerYear', order: 'DESC' },
    },
    {
      id: 6,
      text: t('listingNavigation.orderByOptions.mostGrowthFirst'),
      value: { orderBy: 'revaluationPerYear', order: 'DESC' },
    },
  ]

  const [filtersObj, setFiltersObj] = useState<any[]>([])

  const [selectedOrderByOption, setSelectedOrderByOption] = useState(
    orderByOptions[0],
  )
  const [fundingCompleteToggle, setFundingCompleteToggle] =
    useState<boolean>(false)
  const [searchResults, setSearchResults] = useState<PropertyWithAccess[]>([])
  const [searchTerms, setSearchTerms] = useState<string | undefined>(undefined)
  const [isInputFocused, setInputFocused] = useState(false)

  const handleOrderByChange = (orderBy: any) => {
    onOrderByChange(orderBy)
    setSelectedOrderByOption(orderBy)
  }

  const handlePageChange = (page: number) => {
    onPageChange(page)
  }

  const handleFundingCompleteToggle = (fundingCompleteToggle: boolean) => {
    const filter = {
      filterBy: 'fundingStatus',
      filter: 'funding',
      filterOperator: '=',
    }
    fundingCompleteToggle
      ? setFiltersObj([...filtersObj, filter])
      : setFiltersObj(
          filtersObj.filter(
            (item) =>
              item.filterBy !== filter.filterBy ||
              item.filter !== filter.filter,
          ),
        )

    setFundingCompleteToggle(fundingCompleteToggle)
  }

  const handleInputChange = debounce((event: ChangeEvent<HTMLInputElement>) => {
    setSearchTerms(event.target.value)
  }, 400)

  const handleInputFocus = () => {
    setInputFocused(true)
  }

  const handleInputBlur = () => {
    setInputFocused(false)
  }

  const fetchProperties = async () => {
    const resProperties = await PropertyService.getProperties({
      page: currentPage,
      pageSize: 10,
      lang: locale,
      keyword: searchTerms,
    })
    setSearchResults(resProperties.properties as PropertyWithAccess[])
  }

  useEffect(() => {
    onChangeFilters(filtersObj)
    if (searchTerms && searchTerms.length) {
      fetchProperties()
    } else {
      setSearchResults([])
    }
  }, [filtersObj, searchTerms])

  return (
    <div className="relative">
      <div className="border-b border-neutralGray pb-3">
        <div className="block items-center space-y-2 sm:flex md:flex md:space-x-4 md:space-y-0">
          <div className="relative mr-auto hidden sm:block md:w-96">
            <input
              type="search"
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              className="text-input h-full w-full px-3 py-6 md:w-96"
              placeholder={t('listingNavigation.searchPlaceholder')}
            />
            {isInputFocused && searchResults && searchResults.length ? (
              <ul
                onMouseDown={(event) => event.preventDefault()}
                className="border-1 absolute z-30 mt-1 h-max max-h-96 w-full overflow-y-scroll rounded-xl text-xs"
              >
                {searchResults.map((property) => (
                  <li
                    key={property.id}
                    className="relative select-none border-b bg-neutralWhite"
                  >
                    <NavigationPropertyPreview
                      property={property}
                      onClick={() => onPropertyClick?.(property)}
                    />
                  </li>
                ))}
              </ul>
            ) : (
              ''
            )}
            <div className="pointer-events-none absolute inset-y-0 right-0 flex select-none items-center pr-2">
              <HeroIcons.MagnifyingGlassIcon className="h-5 w-5 text-neutralGray" />
            </div>
          </div>
          <div className="border-1 relative ml-auto flex w-fit rounded-2xl border-neutralGray/20 bg-neutralGray/20 px-3 py-1.5 text-neutralBlack">
            <div className="flex items-center text-sm">
              {t('listingNavigation.completedFinancingToggle')}
            </div>
            <Switch
              checked={fundingCompleteToggle}
              onChange={handleFundingCompleteToggle}
              className={`${
                fundingCompleteToggle ? 'bg-brandLightBlue' : 'bg-neutralGray'
              } relative ml-3 inline-flex h-6 w-11 items-center rounded-full`}
            >
              <span
                className={`${
                  fundingCompleteToggle ? 'translate-x-6' : 'translate-x-1'
                } inline-block h-4 w-4 transform rounded-full bg-white transition`}
              />
            </Switch>
          </div>
        </div>
      </div>
      <div className="flex py-3">
        <div className="flex">
          <div className="relative ml-2">
            <Listbox
              value={selectedOrderByOption}
              onChange={handleOrderByChange}
            >
              <ListboxButton className="relative flex w-full text-sm font-bold text-neutralBlack">
                {selectedOrderByOption.text}
                <span className="pointer-events-none flex items-center">
                  <HeroIcons.ChevronDownIcon
                    className="h-5 w-5"
                    aria-hidden="true"
                  />
                </span>
              </ListboxButton>
              <ListboxOptions className="border-1 absolute z-30 mt-1 max-h-60 w-52 rounded-xl bg-neutralWhite text-xs">
                {orderByOptions.map((orderByOption) => (
                  <ListboxOption
                    key={orderByOption.id}
                    value={orderByOption}
                    className={({ focus }) =>
                      `relative cursor-pointer select-none px-2 py-2 text-neutralBlack hover:bg-brandDarkPurple hover:text-neutralWhite ${
                        focus
                          ? 'bg-brandLightBlue text-white'
                          : 'text-neutralBlack'
                      }`
                    }
                  >
                    {orderByOption.text}
                  </ListboxOption>
                ))}
              </ListboxOptions>
            </Listbox>
          </div>
        </div>
        <div className="flex flex-grow justify-end text-sm text-neutralBlack">
          {/*<button*/}
          {/*  disabled={currentPage === 1}*/}
          {/*  onClick={() => handlePageChange(currentPage - 1)}*/}
          {/*>*/}
          {/*  <HeroIcons.ChevronLeftIcon*/}
          {/*    className={classNames(*/}
          {/*      'h-5 w-5',*/}
          {/*      currentPage === 1 ? 'text-white' : 'text-neutralGray',*/}
          {/*    )}*/}
          {/*  />*/}
          {/*</button>*/}
          {/*<span className="items-center">*/}
          {/*  {t('listingNavigation.pagination.page')} {currentPage}{' '}*/}
          {/*  {t('listingNavigation.pagination.of')} {maxPages}*/}
          {/*</span>*/}
          {/*<button*/}
          {/*  disabled={currentPage === maxPages}*/}
          {/*  onClick={() => handlePageChange(currentPage + 1)}*/}
          {/*>*/}
          {/*  <HeroIcons.ChevronRightIcon*/}
          {/*    className={classNames(*/}
          {/*      'h-5 w-5',*/}
          {/*      currentPage === maxPages ? 'text-white' : 'text-neutralGray',*/}
          {/*    )}*/}
          {/*  />*/}
          {/*</button>*/}
        </div>
      </div>
    </div>
  )
}
